const FIRST_NAMES = [
  'Anna', 'Lukas', 'Maria', 'Jonas', 'Sabine', 'Thomas', 'Julia', 'Michael', 'Katrin', 'Stefan', 'Petra', 'Andreas',
  'Monika', 'Markus', 'Claudia', 'Florian', 'Birgit', 'Tobias', 'Heike', 'Matthias', 'Sandra', 'Christian', 'Nicole',
  'Daniel', 'Verena', 'Sebastian', 'Martina', 'Johannes', 'Elke', 'Peter', 'Ursula', 'Bernd', 'Lena', 'Felix',
  'Karin', 'Jürgen', 'Eva', 'Klaus', 'Renate', 'Dieter'
];
const LAST_NAMES = [
  'Müller', 'Schneider', 'Fischer', 'Weber', 'Meyer', 'Wagner', 'Becker', 'Schulz', 'Hoffmann', 'Koch', 'Bauer',
  'Richter', 'Klein', 'Wolf', 'Schröder', 'Neumann', 'Schwarz', 'Zimmermann', 'Braun', 'Krüger', 'Hofmann', 'Hartmann',
  'Lange', 'Schmitt', 'Werner', 'Krause', 'Meier', 'Lehmann', 'Schmid', 'Schulze', 'Maier', 'Köhler', 'Herrmann',
  'König', 'Walter', 'Mayer', 'Huber', 'Kaiser', 'Fuchs', 'Peters', 'Lang', 'Scholz', 'Möller', 'Weiß', 'Jung', 'Hahn'
];
const STREETS = [
  'Hauptstraße', 'Gartenstraße', 'Bahnhofstraße', 'Schulstraße', 'Bergstraße', 'Dorfstraße', 'Birkenweg', 'Lindenallee',
  'Waldstraße', 'Rosenweg', 'Kirchplatz', 'Ringstraße', 'Mozartstraße', 'Goethestraße', 'Schillerstraße', 'Eichenweg',
  'Am Sonnenhang', 'Parkstraße', 'Wiesenweg', 'Marktplatz', 'Feldstraße', 'Mühlenweg', 'Buchenstraße', 'Ahornweg'
];
const CITIES: [string, string][] = [
  ['80331', 'München'], ['81369', 'München'], ['85540', 'Haar'], ['82166', 'Gräfelfing'], ['85737', 'Ismaning'],
  ['82031', 'Grünwald'], ['85716', 'Unterschleißheim'], ['82110', 'Germering'], ['85609', 'Aschheim'],
  ['85748', 'Garching'], ['82256', 'Fürstenfeldbruck'], ['85221', 'Dachau'], ['85354', 'Freising'],
  ['82515', 'Wolfratshausen'], ['85521', 'Ottobrunn'], ['82008', 'Unterhaching']
];
const PROJECT_PREFIXES = ['Sanierung', 'Umbau', 'Renovierung', 'Ausbau', 'Neubau', 'Modernisierung'];
const COMPANY_SUFFIXES = ['Immobilien GmbH', 'Bau GmbH', 'Hausverwaltung', 'Wohnbau AG', 'Verwaltungs GmbH', 'Projekt GmbH'];

const CURRENCY_ONLY = /^(€|EUR|zł|PLN|\$|USD|CHF|£)$/i;
const AMOUNT = /^-?\d+([.\s ]\d{3})*(,\d+)?$/;
const STREET_WORD = /(straße|strasse|str\.|weg|platz|allee|gasse|ring|damm|ufer|chaussee|steig|hof)\b/i;
const COMPANY_WORD = /\b(GmbH|mbH|AG|KG|UG|OHG|e\.V\.|Immobilien|Hausverwaltung|Verwaltung|Holding|Gruppe|Bau)\b/i;

/** Deterministic 32-bit hash (FNV-1a), the same text always gives the same fake value. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Small seeded generator (mulberry32). */
function random(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function ascii(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

/** Replaces every digit with another one and keeps the rest of the format (separators, currency, sign). */
function scrambleDigits(value: string, rnd: () => number, keepDigits = 0): string {
  let seen = 0;
  return value.replace(/\d/g, digit => {
    seen++;
    if (seen <= keepDigits) return digit;
    return String(Math.floor(rnd() * (seen === 1 ? 9 : 10)) + (seen === 1 ? 1 : 0));
  });
}

/**
 * Returns a fake (German) replacement of a private text: a name, company, address, e-mail, phone, amount or project
 * title is recognized by its shape and replaced with a value of the same kind and a similar length.
 */
export function fakeText(source: string): string {
  const text = source.trim();
  // empty values and placeholders (e.g. '-') are not private
  if (!/[\p{L}\d]/u.test(text) || CURRENCY_ONLY.test(text)) return source;

  const lead = source.slice(0, source.indexOf(text));
  const trail = source.slice(lead.length + text.length);
  const rnd = random(hash(text.toLowerCase()));
  const pick = <T>(list: T[]): T => list[Math.floor(rnd() * list.length)];

  let fake = text;
  const onlyNumber = text.replace(/€|EUR|zł|PLN|\$|USD|CHF|£/gi, '').trim();

  if (text.includes('@')) {
    fake = `${ascii(pick(FIRST_NAMES))}.${ascii(pick(LAST_NAMES))}@beispiel.de`;
  } else if (/^[\d\s.,+\-–()/ ]+$/.test(onlyNumber) && /\d/.test(onlyNumber)) {
    const isAmount = onlyNumber !== text || AMOUNT.test(onlyNumber);
    fake = scrambleDigits(text, rnd, isAmount ? 0 : text.startsWith('+') ? 2 : 1);
  } else if (/\d/.test(text) && (text.includes(',') || STREET_WORD.test(text)) && /[a-zäöüß]{3}/i.test(text)) {
    const [zip, city] = pick(CITIES);
    const street = `${pick(STREETS)} ${Math.floor(rnd() * 90) + 1}`;
    fake = /\b\d{5}\b/.test(text) || text.includes(',') ? `${street}, ${zip} ${city}` : street;
  } else if (COMPANY_WORD.test(text)) {
    fake = `${pick(LAST_NAMES)} ${pick(COMPANY_SUFFIXES)}`;
  } else {
    const words = text.split(/\s+/);
    if (words.length === 1) {
      fake = pick(LAST_NAMES);
    } else if (/\s(und|&)\s/.test(text)) {
      fake = `${pick(FIRST_NAMES)} und ${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
    } else if (words.length <= 3) {
      fake = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
    } else {
      fake = `${pick(PROJECT_PREFIXES)} ${pick(LAST_NAMES)}`;
    }
  }

  if (text.length > 3 && text === text.toUpperCase() && text !== text.toLowerCase()) fake = fake.toUpperCase();
  return lead + fake + trail;
}
